import { Controller } from "@hotwired/stimulus"

// Chooses files from the media library (CustomerPortal::Social::MediaPicker).
// The library pages through a frame in a dialog; choosing a tile adds it to
// the list of chosen files, whose hidden inputs the form submits in order.
// Several files can be reordered by dragging. Every change dispatches
// "social-media-picker:change" so the composer re-checks the post.
export default class extends Controller {
  static targets = ["list", "item", "template", "dialog", "frame", "empty", "count"]
  static values = { multiple: Boolean, name: String, src: String, composer: Boolean }

  connect() {
    this.closeBeforeRender = () => this.close()
    document.addEventListener("turbo:before-render", this.closeBeforeRender)
    this.renumber()
  }

  disconnect() {
    document.removeEventListener("turbo:before-render", this.closeBeforeRender)
  }

  open() {
    if (!this.frameTarget.getAttribute("src")) this.frameTarget.setAttribute("src", this.srcValue)
    this.dialogTarget.showModal()
    this.mark()
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  toggle(event) {
    const tile = event.currentTarget
    const existing = this.find(tile.dataset.id)
    if (existing) {
      existing.remove()
    } else {
      if (!this.multipleValue) this.itemTargets.forEach((item) => item.remove())
      this.listTarget.append(this.build(tile.dataset))
    }
    this.changed()
    if (!this.multipleValue && !existing) this.close()
  }

  remove(event) {
    event.currentTarget.closest("[data-social-media-picker-target=item]").remove()
    this.changed()
  }

  // Searching waits for a pause in typing, then reloads the frame. The box
  // is a plain input, since the picker sits inside the composer's form.
  search(event) {
    clearTimeout(this.searchTimer)
    const input = event.target
    this.searchTimer = setTimeout(() => {
      const url = new URL(input.dataset.searchUrl, window.location.href)
      if (input.value.trim()) url.searchParams.set("q", input.value.trim())
      this.refocusSearch = true
      this.frameTarget.src = url.toString()
    }, 300)
  }

  // Shows which tiles on the current page are chosen, and in what order.
  mark() {
    if (this.refocusSearch) {
      this.refocusSearch = false
      const input = this.frameTarget.querySelector("input[type=search]")
      input?.focus()
      input?.setSelectionRange(input.value.length, input.value.length)
    }
    const ids = this.ids()
    this.element.querySelectorAll("[data-social-media-picker-target=tile]").forEach((tile) => {
      const index = ids.indexOf(tile.dataset.id)
      tile.setAttribute("aria-pressed", index >= 0 ? "true" : "false")
      const badge = tile.querySelector("[data-role=order]")
      if (badge) badge.textContent = this.multipleValue && index >= 0 ? index + 1 : ""
    })
    if (this.hasCountTarget) this.countTarget.textContent = ids.length === 1 ? "1 chosen" : `${ids.length} chosen`
  }

  dragStart(event) {
    if (!this.multipleValue) return
    this.dragged = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    this.dragged.classList.add("opacity-50")
  }

  dragOver(event) {
    if (!this.dragged) return
    event.preventDefault()
    const target = event.currentTarget
    if (target === this.dragged) return
    const after = event.clientX > target.getBoundingClientRect().left + target.offsetWidth / 2
    target.parentNode.insertBefore(this.dragged, after ? target.nextSibling : target)
  }

  drop(event) {
    event.preventDefault()
  }

  dragEnd() {
    if (!this.dragged) return
    this.dragged.classList.remove("opacity-50")
    this.dragged = null
    this.changed()
  }

  // private

  build(data) {
    const item = this.templateTarget.content.firstElementChild.cloneNode(true)
    item.dataset.id = data.id
    const input = item.querySelector("[data-role=value]")
    input.value = data.id
    input.dataset.kind = data.kind
    input.dataset.preview = data.preview
    input.dataset.title = data.title
    item.querySelector("[data-role=thumb]").src = data.preview
    item.querySelector("[data-role=title]").textContent = data.title
    return item
  }

  find(id) {
    return this.itemTargets.find((item) => item.dataset.id === String(id))
  }

  ids() {
    return this.itemTargets.map((item) => item.dataset.id)
  }

  changed() {
    this.renumber()
    this.mark()
    this.dispatch("change")
  }

  renumber() {
    this.itemTargets.forEach((item, index) => {
      const badge = item.querySelector("[data-role=order]")
      if (badge) badge.textContent = index + 1
    })
    this.emptyTarget.hidden = this.itemTargets.length > 0
  }
}
